<script lang="ts">
  /**
   * Вибір іконки розділу. Замінив EmojiPicker (D6 плану переносу).
   *
   * Чому не емодзі: дизайн перейшов на єдиний набір Lucide, і емодзі в
   * ньому виглядали чужорідно — свій стиль, свій колір, різна вага на
   * різних платформах. Іконка ж бере колір теми й ту саму товщину лінії,
   * що решта інтерфейсу.
   *
   * Набір навмисно короткий (CATEGORY_ICON_NAMES): зі спрайту сюди йдуть
   * лише «тематичні» іконки, а не службові стрілки й хрестики. Бекенд
   * перевіряє тільки форму слага — повний список живе на фронті.
   */
  import { CATEGORY_ICON_NAMES } from '../lib/icons';
  import type { IconName } from '../lib/icons';
  import Icon from './Icon.svelte';

  interface Props {
    value: IconName;
  }
  let { value = $bindable() }: Props = $props();

  let open = $state(false);

  function pick(name: IconName): void {
    value = name;
    open = false;
  }
</script>

<button type="button" class="icon-cell" onclick={() => (open = true)} aria-label="Обрати іконку">
  {#if value}
    <Icon name={value} size="lg" />
  {:else}
    <Icon name="plus" size="lg" />
  {/if}
</button>

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="ip-backdrop" onclick={() => (open = false)}></div>
  <div class="ip-sheet" role="dialog" aria-modal="true" aria-label="Іконка розділу">
    <div class="ip-head">
      <div class="ip-title">Оберіть іконку</div>
      <button
        type="button"
        class="ip-close"
        onclick={() => (open = false)}
        aria-label="Закрити"
      >
        <Icon name="x" />
      </button>
    </div>
    <div class="ip-grid">
      {#each CATEGORY_ICON_NAMES as name (name)}
        <button
          type="button"
          class="ip-option"
          class:active={value === name}
          onclick={() => pick(name)}
          aria-label={name}
          aria-pressed={value === name}
        >
          <Icon {name} size="lg" />
        </button>
      {/each}
    </div>
    <div class="ip-safe-bottom"></div>
  </div>
{/if}

<style>
  .icon-cell {
    width: 48px;
    height: 48px;
    border: 1px solid var(--border2);
    border-radius: 10px;
    background: var(--bg);
    cursor: pointer;
    padding: 0;
    font-family: inherit;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text);
    transition: border-color 0.15s;
  }
  .icon-cell:hover {
    border-color: var(--outline);
  }

  .ip-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 1000;
  }
  .ip-sheet {
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: min(100%, 480px);
    max-height: 85vh;
    background: var(--bg);
    border-radius: 16px 16px 0 0;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.25);
    animation: sheet-up 0.25s var(--ease);
  }
  @keyframes sheet-up {
    from {
      transform: translate(-50%, 100%);
    }
    to {
      transform: translate(-50%, 0);
    }
  }
  .ip-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 0.5px solid var(--border);
    flex-shrink: 0;
  }
  .ip-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--text);
  }
  .ip-close {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--bg2);
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: inherit;
  }
  .ip-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));
    gap: 10px;
    padding: 16px 18px;
    overflow-y: auto;
  }
  .ip-option {
    aspect-ratio: 1;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--bg);
    color: var(--text);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: inherit;
    transition:
      border-color 0.15s,
      background 0.15s;
  }
  .ip-option:hover {
    border-color: var(--outline);
  }
  .ip-option.active {
    background: var(--pu);
    border-color: var(--pu);
    color: var(--on-pu);
  }
  .ip-safe-bottom {
    flex-shrink: 0;
    height: env(safe-area-inset-bottom);
    background: var(--bg);
  }
</style>
